import { joiResolver } from '@hookform/resolvers/joi';
import { Box, Container, Grid, Link, Paper, Typography } from '@mui/material';
import Joi from 'joi';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import settings from '../../../global/config/settings';
import { API } from '../../../global/config/api';
import { BaseHeader, XsButton, XsTextField } from '../../../global/ui';
import { useServerCall } from '../../../global/helpers/hooks';
import { isPasswordValid } from '../../../global/helpers/validators';
import GlobalStore from '../../../global/stores/GlobalStore';
import UIStore from '../../../global/stores/UIStore';
import LoginStore from './LoginStore';

interface IResetPasswordForm {
  password: string;
  conf_psw: string;
}

function ResetPasswordContainer() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const schema = Joi.object<IResetPasswordForm>({
    password: Joi.string()
      .custom((v, h) => (isPasswordValid(v) ? v : h.error('any.invalid')))
      .required(),
    conf_psw: Joi.string()
      .custom((v, h) => (isPasswordValid(v) ? v : h.error('any.invalid')))
      .required()
  });

  const form = useForm<IResetPasswordForm>({
    resolver: joiResolver(schema),
    defaultValues: { password: '', conf_psw: '' }
  });
  const { control, handleSubmit } = form;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token') ?? '';
    LoginStore.token = urlToken;
  }, []);

  const resetFinishCall = useServerCall<{ password: string; as_login: boolean }, any>({
    key: 'resetPasswordFinish',
    mode: 'control',
    request: { method: 'POST' },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: () => {
      GlobalStore.loading(false);
      UIStore.setNotificationMessage(t('login.reset_success'), 'success');
      history.push('/login');
    },
    onError: () => {
      GlobalStore.loading(false);
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    if (data.password !== data.conf_psw) {
      UIStore.setNotificationMessage(t('register.password_mismatch'), 'warning');
      return;
    }
    GlobalStore.loading(true);
    const recaptchaToken = await LoginStore.getRecaptchaToken('client_action_consume');
    resetFinishCall.run({
      url: API.actionConsume(LoginStore.token ?? '', recaptchaToken),
      data: { password: data.password, as_login: true }
    });
  });

  return (
    <>
      <BaseHeader />
      <Container maxWidth='lg'>
        <Grid container justifyContent='center'>
          <Grid item style={{ maxWidth: 450, width: 450 }}>
            <Grid container direction='column' justifyContent='center' style={{ height: '65vh' }}>
              <Grid container direction='column' spacing={3}>
                <Grid item className='textCenter'>
                  <Typography variant='h3'>{t('register.set_password_title')}</Typography>
                </Grid>
                <Grid item>
                  <Paper variant='elevation' elevation={2} style={{ backgroundColor: 'transparent' }}>
                    <Box p={2}>
                      <form onSubmit={onSubmit}>
                        <FormProvider {...form}>
                          <Grid item container direction='column' spacing={2}>
                            <Grid item>
                              <Typography variant='body2'>
                                <span className='bold'>{t('register.password_criteria_title')}</span>
                                <ul>
                                  <li>{t('register.password_criteria1')}</li>
                                  <li>{t('register.password_criteria2')}</li>
                                  <li>{t('register.password_criteria3')}</li>
                                  <li>{t('register.password_criteria4')}</li>
                                  <li>{t('register.password_criteria5')}</li>
                                </ul>
                              </Typography>
                              <Typography variant='body2'>{t('register.password_generate_hint')}</Typography>
                            </Grid>
                            <Grid item>
                              <Controller
                                name='password'
                                control={control}
                                render={({ field }) => (
                                  <XsTextField
                                    {...field}
                                    type='password'
                                    autoFocus
                                    label={t('register.password_label')}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item>
                              <Controller
                                name='conf_psw'
                                control={control}
                                render={({ field }) => (
                                  <XsTextField {...field} type='password' label={t('register.conf_psw_label')} />
                                )}
                              />
                            </Grid>
                            {GlobalStore.enabledRecaptchaV2 && (
                              <Grid item>
                                <ReCAPTCHA
                                  sitekey={settings.SITE_KEY_V2 ?? ''}
                                  onChange={(tok) => (GlobalStore.tokenRecaptchaV2 = tok ?? undefined)}
                                />
                              </Grid>
                            )}
                            <Grid item>
                              <XsButton type='submit' variant='contained' label='register.reset_password_submit' />
                            </Grid>
                            <Grid item>
                              <Link variant='body2' className='pointer' onClick={() => history.push('/login')}>
                                {t('login.submit')}
                              </Link>
                            </Grid>
                          </Grid>
                        </FormProvider>
                      </form>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default observer(ResetPasswordContainer);
